import capitalize from 'lodash/capitalize';
import toUpper from 'lodash/toUpper';
import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';
import { format, add } from 'date-fns';
import isNumber from 'lodash/isNumber';
import { isJSMProduct, isJPDProduct, isStatuspage, isJiraProduct } from '@atlassian/cssxp-utils-product';
import { RegexPatterns, RelationshipPathType } from '@atlassian/cssxp-typescommon';
import { FEDRAMP_MODERATE, getMicrosPerimeter } from '@atlassian/temp-shared';
import { EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import { SortDirection, SortType } from '@css-xp/graphtypes/src/generated-types-new';
import { ProductOffering } from '@css-xp/graphtypes/src/generated-types-sxp';
import {
  AGGREGATOR_PARTNER,
  CORPORATE_RESELLER_PARTNER,
  DATE_FORMAT,
  COMMERCIAL_PRICE_ELIGIBILITY,
  BITBUCKET_PRODUCT_ID,
} from '@css-xp/ppc-constants';
import { convertArrayToCSV } from 'convert-array-to-csv';
import FileSaver from 'file-saver';
import { getIdentityUrl, getWACUrl } from './ppc-login-utils';
import {
  getChargeElementText,
  isMultiInstanceProductOffering,
  MultiInstanceRelationshipPathType,
} from './ppc-product-utils';

export type DateTimeStamp = number | string | undefined;
export type IncludeOfferingType = Pick<
  ProductOffering,
  'offeringGroup' | 'relationshipRequirements' | 'id' | 'offeringName'
>;

export const ROVO_PAID_OFFERING_ID = 'e7de08be-3d20-423e-b928-dd1fd325da7a';

export const getSearchTextVariants = (key: string, value: string) => {
  const uniqueValues = getSearchTextVariantsForASearchTerm(value);
  return [...uniqueValues].map((uniqueValue) => ({ [key]: uniqueValue }));
};

export const getSearchTextVariantsForASearchTerm = (searchTerm?: string | null) => {
  // Using `capitalize`, `startCase`, `toLowerCase` and `toUpperCase` to improve
  // the user experience and get results even if the search text was wrongly cased,

  if (!searchTerm) {
    return new Set([]);
  }

  // To improve: If we can ascertain the pattern of 'key' param, say 'invoiceNumber'
  // is always CAPS, then we can use only the applicable text variants
  const startCaseSearchText = searchTerm.replace(/\w+/g, capitalize);
  const upperCasedText = searchTerm.toUpperCase();
  const lowerCasedText = searchTerm.toLowerCase();
  const capitalizedText = capitalize(searchTerm);
  return new Set([searchTerm, capitalizedText, startCaseSearchText, upperCasedText, lowerCasedText]);
};

export const isEmailAddress = (word: string) => {
  return isEmail(word);
};

export const isURLAddress = (string: string) => {
  return isURL(string);
};

export const isCCPEntitlement = (string: string) => {
  return RegexPatterns.CCP_ENTITLEMENT_PATTERN.test(string);
};

export const getFormattedDateFromTimeStamp = (dateTimeStamp?: DateTimeStamp, skipTimeFromDate?: boolean) => {
  // if no timestamp passed return empty string early without any condition check
  if (!dateTimeStamp) {
    return '';
  }

  const formattedDate = new Date((dateTimeStamp as number) / 1000).toISOString();

  if (skipTimeFromDate) {
    return formattedDate.slice(0, 10);
  }

  return formattedDate;
};

export const enterpriseOfferingList = [
  '0b9052f7-6939-4894-92a6-6ebd62f5c4c7', // Enterprise - Confluence (Cloud)
  '9b4104c4-8f72-4365-aacb-ddb576134da0', // Enterprise - Jira Service Management (Cloud)
  '94f47374-030f-46c2-9d8f-df7d2b9a7f5e', // Enterprise - Jira Software (Cloud)
];

export const loomSortedOfferingList = [
  '67737b67-caad-48af-ac36-01a64e9f220b', // Starter
  'd009c283-d868-4d90-aa1e-17609d515f0f', // Business
  '3f72cea3-aa1c-446c-a3e4-d09fed86a62c', // Business + AI
];

export const compassPremiumOfferingId = '4b41c4e3-7f26-4182-a12b-8ed7468350c4';

export const JSMRepackagingStandardOffering = [
  '99bcbf6c-4fff-4a45-8303-a854e7fbd17c',
  '5d465fb3-d8ac-3049-99c7-38db2d92070c',
];
export const JSMDeprecatedStandardOffering = [
  '396efb16-749b-41fa-9cd1-ad10cfefa1d0',
  '7e59fd13-4e66-4a68-983b-dcf1feef38af',
];

export const JPDOfferingList = {
  standalone: [
    '35b9268e-66e2-45fb-81d3-dacd1e31831a', // Standard - JPD Standalone
    'd843dd9e-23c5-4d27-af44-5009d568cb6e', // Free - JPD Standalone
    'f0b24ca7-c3d8-4cac-b5ed-60f4152d4dcd', // Premium - JPD Standalone
  ],
  family: [
    'cb8bc8af-491f-41be-8502-ae5de94d04f0', // Standard - JPD Family Offering
    'd2b66d44-0754-4c89-8d3b-c8f9a8102184', // Free - JPD Family Offering
    'baad3822-06d9-4ec4-a02b-dc6e596f0ca0', // Premium - JPD Family Offering
  ],
};

// FIXME: Use a server config based approach to exclude these offerings
const offeringExcludeList = [
  'b989c33c-1711-11ed-861d-0242ac120002', // Free Standard
  '1c201b9d-1615-4f8b-b9d4-b75be021a2aa', // Free Premium - Jira Service Management (Cloud)
  'e5fd081c-3bde-4935-bf83-3c680da0e9fd', // Beta - Atlas
  '7af07cb3-d4e5-473e-87b3-3ae3bc9248a6', // Beta - Compass
  '0329b71d-e75c-3b95-9651-3db472948ddb', // Standard (Container-Test) Sandbox - Jira Product Discovery
  '4d9a6c44-50cc-35bc-9eee-f1b675cd3a5c', // Free (Container-Test) Sandbox - Jira Product Discovery
  'a26ee53d-d28b-49c7-b97d-1a80b387fd60', // Beta - Jira Product Discovery
  'cb8bc8af-491f-41be-8502-ae5de94d04f0', // Standard (Container-Test) - Jira Product Discovery
  'd2b66d44-0754-4c89-8d3b-c8f9a8102184', // Free (Container-Test) - Jira Product Discovery
  '0b9052f7-6939-4894-92a6-6ebd62f5c4c7', // Enterprise - Confluence (Cloud)
  '39605741-b92f-4763-8229-7bba2d16433c', // Enterprise Instance - Confluence (Cloud)
  'e1bd43e3-8c9f-40e9-886d-ca030f1d91c8', // Premium Sandbox - Confluence (Cloud)
  '556fddf5-ecee-3e8f-ae68-c64f3d054934', // Enterprise Instance Sandbox - Confluence (Cloud)
  '0fa14518-ac43-4701-a3cd-dc2d46556554', // Free Sandbox - Jira Service Management (Cloud)
  '11c62484-1632-4ec2-a0c9-5261e2351477', // Premium Sandbox - Jira Service Management (Cloud)
  '4021727f-e6d1-47ad-be64-2367755f5c51', // Enterprise Instance - Jira Service Management (Cloud)
  '7e59fd13-4e66-4a68-983b-dcf1feef38af', // Standard Sandbox - Jira Service Management (Cloud)
  '9b4104c4-8f72-4365-aacb-ddb576134da0', // Enterprise - Jira Service Management (Cloud)
  '9c222aa8-cb9b-30d2-85aa-faa0cefee292', // Enterprise Instance Sandbox - Jira Service Management (Cloud)
  '3767358f-f995-3170-913f-e62050412ec8', // Enterprise Instance Sandbox - Jira Software (Cloud)
  '5f97b7c6-c0a2-4811-ab57-091d2ba2ec41', // Premium Sandbox - Jira Software (Cloud)
  '799958b5-36bb-4a3d-9289-9fd2986352a7', // Enterprise Instance - Jira Software (Cloud)
  '94843b2d-b3b7-4aec-9357-f568106c2f0c', // Free Sandbox - Jira Software (Cloud)
  '94f47374-030f-46c2-9d8f-df7d2b9a7f5e', // Enterprise - Jira Software (Cloud)
  'f7822008-8e2a-4394-b023-a76c2755ed4b', // Standard Sandbox - Jira Software (Cloud)
  '06f3a842-9973-3446-afbd-3f832b436d32', // Standard Sandbox - Jira Work Management (Cloud)
  '84282cc4-17fc-34c3-85ab-ece9082fa15f', // Free Sandbox - Jira Work Management (Cloud)
  'fd029169-a025-367a-9170-56e9d5d4c454', // Premium Sandbox - Jira Work Management (Cloud)
  'c059cbe9-da6e-3feb-855f-f97e9351263b', // Premium Demo - Confluence (Cloud)
  '78cdbf6b-8828-32dc-849b-1b1dd24ff5b5', // Premium Demo - JSW (Cloud)
  'fa8da527-4f95-3500-b54a-74c2a507e9c9', // Standard Demo - Compass
  '004b535a-337a-3594-909e-488c0296c716', // Standard Demo - JPD
  'b055ff89-0a50-3e50-87a8-6eb448f1dce3', // Premium Demo - Jira Service Management (Cloud)
  'f0b24ca7-c3d8-4cac-b5ed-60f4152d4dcd', // Premium JPD - Jira Product Discovery
  '8d09af0c-7d7b-31cd-97ca-11c88f36e2bf', // Premium Sandbox - Jira Product Discovery
  '441693e6-3329-4ca3-b2d6-d832da7f3566', // Jira Service Management FedRAMP Moderate - Instance
  'c675f4ff-3eaf-4445-9294-1494ff23fcf4', // Jira Service Management FedRAMP Moderate - Enterprise
  'b9015f34-200f-4d57-be87-b9025361b78a', // Jira FedRAMP Moderate - Instance
  'dc612284-8b57-488e-9cbd-e8a73558de9a', // Jira FedRAMP Moderate - Enterprise
  'd7ee0587-a526-435e-b108-8ac020070545', // Confluence FedRAMP Moderate - Instance
  'b29a105c-7a8a-4e7d-a831-01085240df68', // Confluence FedRAMP Moderate - Enterprise
  'e0afd14f-de68-3df5-b3b2-7f7e16917271', // Free Standard Sandbox
  '5d465fb3-d8ac-3049-99c7-38db2d92070c', // Standard Free
  '57222d5e-6f25-3323-878a-e7221b329c68', // Loom Business + AI Demo
  '4b41c4e3-7f26-4182-a12b-8ed7468350c4', // Compass premium
  'c85d9554-5c1b-3d3b-adba-ea4104c17a5c', // Compass premium sandbox
  '6ec5dac2-9447-4979-bdba-98cb51751687', // Loom - Enterprise
];

const JPDPremiumOfferings = ['f0b24ca7-c3d8-4cac-b5ed-60f4152d4dcd', 'baad3822-06d9-4ec4-a02b-dc6e596f0ca0'];

export const isNotInOfferingExcludeList = (
  offering: any,
  isEnterpriseOfferingEnabled?: boolean,
  isJSMRepackagingEnabled?: boolean,
  isCompassPremiumEnabled?: boolean
) => {
  const offeringId = offering?.id;
  let excludedList = [...offeringExcludeList];
  if (isEnterpriseOfferingEnabled) {
    excludedList = excludedList.filter((offering) => !enterpriseOfferingList.includes(offering));
  }
  if (isCompassPremiumEnabled) {
    excludedList = excludedList.filter((offering) => offering !== compassPremiumOfferingId);
  }
  if (isJSMRepackagingEnabled) {
    excludedList.push(...JSMDeprecatedStandardOffering);
  } else {
    excludedList.push(...JSMRepackagingStandardOffering);
  }
  return !excludedList.includes(offeringId);
};

const isAppOffering = (offering: IncludeOfferingType): boolean => {
  return (
    offering?.relationshipRequirements?.some(
      (requirement) =>
        requirement?.side?.toUpperCase() === 'TO' && requirement?.type?.toUpperCase() === 'APP_DEPENDENCE'
    ) ?? false
  );
};

const isInstancesOffering = (offering: IncludeOfferingType): boolean => {
  //collection instances offerings are for loom
  // FIXME :: Use enum for this
  const isCollectionInstanceOffering =
    offering?.relationshipRequirements?.some(
      (requirement) => requirement?.side?.toUpperCase() === 'TO' && requirement?.type?.toUpperCase() === 'COLLECTION'
    ) ?? false;

  //Multiple instance offerings are for Rovo
  const isMultiInstanceOffering =
    offering?.relationshipRequirements?.some(
      (requirement) =>
        requirement?.side === 'TO'?.toUpperCase() &&
        requirement?.type?.toUpperCase() === MultiInstanceRelationshipPathType.MULTI_INSTANCE
    ) ?? false;

  // FIXME :: Use enum for this
  const isEnterpriseInstanceOffering =
    offering?.relationshipRequirements?.some(
      (requirement) => requirement?.side?.toUpperCase() === 'TO' && requirement?.type?.toUpperCase() === 'ENTERPRISE'
    ) ?? false;

  return isCollectionInstanceOffering || isMultiInstanceOffering || isEnterpriseInstanceOffering;
};

export const isSandboxOffering = (offering: any) =>
  !!offering?.derivedFromOffering || !!offering?.offeringGroup?.toUpperCase().includes('SANDBOX');

const excludedListV2 = [
  '6ec5dac2-9447-4979-bdba-98cb51751687', // Loom - Enterprise
];

type OfferingGroupType = 'DEFAULT' | 'PUBLIC' | 'PRIVATE' | 'SANDBOX' | 'DEMO' | 'AUDIENCE-SPECIFIC';

export const isOfferingIncluded = (
  offering: IncludeOfferingType,
  config?: {
    productId?: string;
    isJSMRepackagingEnabled?: boolean;
    isCompassPremiumEnabled?: boolean;
    isEnterpriseOfferingEnabled?: boolean;
    isJPDPremiumOfferingEnabled?: boolean;
    isJPDAsJiraFamilyEnabled?: boolean;
    isAppEditionSupportEnabled?: boolean;
    isMultiInstanceAppEnabled?: boolean;
    isBitbucketProductEnabled?: boolean;
  }
) => {
  const offeringId = offering?.id;

  if (excludedListV2.includes(offeringId)) {
    return false;
  }

  if (config?.isBitbucketProductEnabled && config.productId === BITBUCKET_PRODUCT_ID) {
    if (offering.offeringName?.toUpperCase() === EntitlementEdition.PREMIUM) {
      return true;
    }
    return false;
  }

  if (config?.isAppEditionSupportEnabled && isAppOffering(offering)) {
    if (isInstancesOffering(offering) || isSandboxOffering(offering)) {
      return false;
    }
    return true;
  }

  // Checking if offering id belongs to JPD offering
  if (
    Object.values(JPDOfferingList)
      .flatMap((item) => item)
      .includes(offeringId)
  ) {
    // if JPD Premium offering is not enabled then show JPD Other standalone offerings
    if (!config?.isJPDPremiumOfferingEnabled && JPDPremiumOfferings.includes(offeringId)) {
      return false;
    }

    // if JPD as Jira Family is enabled then show JPD Family offerings
    if (config?.isJPDAsJiraFamilyEnabled && isOfferingBelongsToJiraFamily(offering)) return true;

    // if JPD as Jira Family is not enabled then show JPD Standalone offerings
    if (!config?.isJPDAsJiraFamilyEnabled && JPDOfferingList.standalone.includes(offeringId)) return true;
    return false;
  }

  if (
    offering?.offeringGroup &&
    // status-page has private, public and audience specific offerings
    ['DEFAULT', 'PUBLIC', 'PRIVATE', 'AUDIENCE-SPECIFIC'].includes(
      offering.offeringGroup.toUpperCase() as OfferingGroupType
    )
  ) {
    if (isInstancesOffering(offering)) {
      return false;
    }

    //If compass premium enabled then only show compass premium
    //if JSM repackaging enabled then do not how deprecated offerings
    if (
      (!config?.isCompassPremiumEnabled && offeringId === compassPremiumOfferingId) ||
      (config?.isJSMRepackagingEnabled && JSMDeprecatedStandardOffering.includes(offeringId)) ||
      (!config?.isJSMRepackagingEnabled && JSMRepackagingStandardOffering.includes(offeringId))
    ) {
      return false;
    }

    return !excludedListV2.includes(offeringId);
  } else if (offering?.offeringGroup === '') {
    // if app allow every offering
    // for standard plan of app, offering group is empty
    // for advanced plan of app, offering group is advanced
    // for standard multi-instance app, offering group is empty
    if (isAppOffering(offering)) {
      return !isInstancesOffering(offering);
    }
    if (
      config?.isMultiInstanceAppEnabled &&
      !!config?.productId &&
      isMultiInstanceProductOffering(offering, config.productId)
    ) {
      return true;
    }
    //For enterprise offering we are getting offeringGroup as ''
    if (config?.isEnterpriseOfferingEnabled && enterpriseOfferingList.includes(offeringId)) {
      return true;
    }
  }

  return false;
};

export const pluralize = (count: number, noun: string, suffix = 's') => {
  return `${count} ${noun}${count !== 1 ? suffix : ''}`;
};

export const addDaysToDate = (date: Date, days: number): Date => {
  return add(date, {
    days,
  });
};

export const formatDateToMonthDay = (timestamp: number): string => {
  return format(timestamp, DATE_FORMAT);
};

export const getChargeElement = ({
  productId,
  labelPlural = false,
  edition,
}: {
  productId: string | undefined | null;
  labelPlural?: boolean;
  edition?: string | undefined | null;
}) => {
  if (isStatuspage(productId)) {
    if (edition === 'Audience-Specific') {
      return 'tiers';
    } else {
      return 'units';
    }
  }

  if (isJSMProduct(productId || '')) {
    return labelPlural ? 'agents' : 'agent';
  }
  if (isJPDProduct(productId)) {
    return labelPlural ? 'creators' : 'creator';
  }
  return labelPlural ? 'users' : 'user';
};

export const getChargeElementV2 = ({
  productId,
  edition,
  labelPlural = false,
  chargeElement = 'user',
}: {
  productId?: string | undefined;
  edition?: string | undefined;
  labelPlural?: boolean;
  chargeElement?: string | undefined;
}) => {
  if (isStatuspage(productId)) {
    if (edition === 'Audience-Specific') {
      return 'tiers';
    } else {
      return 'units';
    }
  }

  if (isJSMProduct(productId || '')) {
    return labelPlural ? 'agents' : 'agent';
  }
  if (isJPDProduct(productId)) {
    return labelPlural ? 'creators' : 'creator';
  }
  if (toUpper(edition) === 'ADD-ON') {
    return getChargeElementText(chargeElement);
  }
  return labelPlural ? `${chargeElement}s` : chargeElement;
};
/*
 * This function is used to get the filter options for invoice groups query whose result is then used to get the orders.
 * Used in
 * 1. OrderHistoryTable (partner-purchase-center/components/order-history-table/src/order-history-table.tsx)
 * 2. OrdersSearch (partner-purchase-center/pages/search/src/ui/components/orders-search.tsx).
 *
 * @param transactionAccountId
 * @param searchInput - The search input entered by the user.
 * @returns filter options object.
 */
export const getFilterOptionsForInvoiceGroupsQuery = (transactionAccountId: string, searchInput: string) => {
  const propertiesToSearch = [
    'shipToName',
    'shipToAddress1',
    'shipToAddress2',
    'shipToCity',
    'shipToState',
    'shipToCountry',
    'shipToPostalCode',
  ];

  return {
    and: [
      {
        key: 'transactionAccountId',
        value: transactionAccountId,
      },
      {
        or: propertiesToSearch.map((property) => ({
          key: property,
          value: searchInput,
        })),
      },
    ],
  };
};

export const getSortDirectionMatchFromSortOptions = (
  sortOptions: SortType,
  sortKey: string,
  sortDirection: SortDirection
) => {
  const keyIndex = sortOptions?.sortFields?.findIndex((key) => key === sortKey);
  if (isNumber(keyIndex) && keyIndex >= 0) {
    return sortOptions?.sortFieldsDirections?.[keyIndex] === sortDirection;
  }
};

export const downloadAsJSON = (data: Record<string, unknown>, fileName: string) => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  FileSaver.saveAs(blob, `${fileName}.json`);
};

export const downloadAsCSV = ({
  header,
  data,
  fileName,
}: {
  header: Array<string>;
  data: Array<Array<string | number>>;
  fileName: string;
}) => {
  const csv = convertArrayToCSV([header, ...data]);
  const csvFile = new Blob([csv], { type: 'text/csv' });
  const name = `${fileName}-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`;
  FileSaver.saveAs(csvFile, name);
};

export const getProvisioningUrl = (newSiteOrderId: string, transactionAccountId: string, email: string): string => {
  const queryParams = [
    `login_hint=${encodeURIComponent(email)}`,
    `prompt=none`,
    `continue=${encodeURIComponent(
      `${getWACUrl()}/buy/cloud/site-setup?orderId=${newSiteOrderId}&transactionAccount=${transactionAccountId}`
    )}`,
  ];
  return `${getIdentityUrl()}/login?${queryParams.join('&')}`;
};

export const getStaleReasonText = (staleReason: string | undefined) => {
  // Mappings: https://hello.atlassian.net/wiki/spaces/~15947515/pages/2414015344/Quote+Invalid+Reason+Mappings

  switch (staleReason) {
    case 'BILL_TO_UPDATED':
      return 'Bill-to address has been updated';
    case 'SHIP_TO_UPDATED':
      return 'Ship-to address has been updated';
    case 'QUOTE_LINE_INVALID':
      return 'Quote line is invalid';
    case 'BILL_ON_ACCEPTANCE_INVALID':
      return 'Billed-on acceptance is invalid';
    case 'ENTITLEMENT_UPDATED':
    default:
      return 'Subscription has been updated';
  }
};

export const downloadFile = (url: string, fileName: string) => {
  const anchorTag = document.createElement('a');
  anchorTag.href = url;
  anchorTag.download = fileName;
  anchorTag.click();
};

export const getSearchParams = (param: string) => {
  const searchParams = new URLSearchParams(window.location?.search);
  return searchParams.get(param);
};

export const removeAllSearchParams = () => {
  window.history.replaceState({}, '', window.location.href.split('?')[0]);
};

export const convertBase64ToBytes = (base64: string) => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(Array.from(binaryString, (ch) => ch.charCodeAt(0)));
  return bytes;
};

export const downloadJSONFromBase64 = (base64: string, fileName: string) => {
  const bytes = convertBase64ToBytes(base64);
  const jsonString = new TextDecoder().decode(bytes);
  const jsonData = JSON.parse(jsonString);
  const formattedJsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([formattedJsonString], { type: 'application/json' });
  FileSaver.saveAs(blob, fileName);
};

export const getCorrectProgramLevelGlobal = (selectedPartnerAccountData: {
  partnerType: string;
  programLevelGlobal: string | null;
}) => {
  return selectedPartnerAccountData?.partnerType === AGGREGATOR_PARTNER ||
    selectedPartnerAccountData?.partnerType === CORPORATE_RESELLER_PARTNER
    ? null
    : selectedPartnerAccountData?.programLevelGlobal;
};

export const getSupportTicketDetails = (supportTicket?: string | null) => {
  const urlRegex = /^(https?:\/\/)?[^\s]+\.com/;
  const supportUrlRegex = /\/.+$/;
  const httpRegex = /https?:\/\//;
  const supportTicketUrl = supportTicket && supportTicket.replace(httpRegex, '');
  const isValidUrl = !!(supportTicketUrl && urlRegex.test(supportTicketUrl));
  const isValidSupportTicketUrl = isValidUrl && supportUrlRegex.test(supportTicketUrl);
  const supportTicketNumber = isValidSupportTicketUrl ? supportTicketUrl.split('/').pop() : null;
  const redirectUrl = isValidUrl ? `https://${supportTicketUrl}` : undefined;
  return { supportTicketUrl, isValidUrl, supportTicketNumber, redirectUrl };
};

export const splitAndCapitalize = (inputText?: string | null) => {
  if (!inputText) {
    return inputText;
  }

  return inputText
    .split('_')
    .map((word) => capitalize(word))
    .join(' ');
};

export const isProductBelongsToJiraFamily = (
  productId: string | null | undefined,
  config?: {
    isJPDAsJiraFamilyEnabled?: boolean;
  }
) => {
  if (isJiraProduct(productId)) return true;

  if (config?.isJPDAsJiraFamilyEnabled && isJPDProduct(productId)) return true;

  return false;
};

export const isOfferingBelongsToJiraFamily = (offering: IncludeOfferingType | null | undefined) => {
  return offering?.relationshipRequirements?.some(
    (relationship) => relationship?.type === RelationshipPathType.FAMILY_CONTAINER
  );
};

export const isExistingStandaloneJPDProduct = (offeringId?: string) => {
  return !!(offeringId && JPDOfferingList.standalone.includes(offeringId));
};

export const numberToWords = (n: number) => {
  const special = [
    'zeroth',
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
    'eleventh',
    'twelfth',
    'thirteenth',
    'fourteenth',
    'fifteenth',
    'sixteenth',
    'seventeenth',
    'eighteenth',
    'nineteenth',
  ];
  const deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

  if (n < 20) return special[n];
  if (n % 10 === 0) return deca[Math.floor(n / 10) - 2] + 'ieth';
  return deca[Math.floor(n / 10) - 2] + 'y-' + special[n % 10];
};

export const isCommercialPricingEligibility = (pricingEligibility: string) =>
  pricingEligibility.toLowerCase() === COMMERCIAL_PRICE_ELIGIBILITY.toLowerCase();

export const isFedrampPerimeter = () => {
  const env = getMicrosPerimeter();

  if (env) {
    return env === FEDRAMP_MODERATE;
  }

  const matches = window.location?.hostname?.match(
    /atlassian-us-gov-mod\.(com|net)|atlassian-us-gov\.(com|net)|atlassian-fex\.(com|net)|atlassian-stg-fedm\.(com|net)/
  );

  return matches ? matches.length > 0 : false;
};
